import {
  useEffect,
  useRef,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import Skeleton from "react-loading-skeleton";

import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";

import { AppleMapProps, AppleMapRef, MapLocation, MapBounds } from "./types";
import { loadMapKitScript, calculateRegionForAnnotations, syncRender } from "./utils";

import styles from "./styles.module.scss";
import { Button, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { useTranslation } from "react-i18next";


const getAppleMapToken = () => {
  // The same backend deployment is serving both cap-rx and judi.cloud domains, so it can't give us the correct token itself.

  const token = site["appleMapToken"] || process.env.CAPITALRX_APPLE_MAP_TOKEN || process.env.REACT_APP_TEMP_APPLE_MAP_TOKEN;

  if (window.location.host.includes("judibenefits")) {
    const alternativeToken = site["alternativeAppleMapToken"];

    return alternativeToken || token;
  }

  else if (window.location.host.includes("judi.cloud")) {
    const alternativeToken = site["judiCloudAppleMapToken"];

    return alternativeToken || token;
  }

  return token;
};


const AppleMap = forwardRef<AppleMapRef, AppleMapProps>(
  (
    {
      token = getAppleMapToken(),
      locations = [],
      className,
      style,
      mapType = "standard",
      showsCompass = "adaptive",
      showsMapTypeControl = false,
      showsZoomControl = true,
      showsUserLocationControl = false,
      showsPointsOfInterest = true,
      showsScale = "adaptive",
      language,
      padding = { top: 50, right: 50, bottom: 50, left: 50 },
      center,
      onMapLoad,
      onSearchArea,
      renderCalloutContent,
      renderCalloutRightAccessory,
      renderCalloutLeftAccessory,
      isLoading,
    },
    ref,
  ) => {
    const { t: translate, i18n } = useTranslation();

    const [isLoaded, setIsLoaded] = useState(false);
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<mapkit.Map | null>(null);
    const annotationsRef = useRef<mapkit.MarkerAnnotation[]>([]);
    const mapkitRef = useRef<typeof mapkit | null>(null);

    const createAnnotation = useCallback(
      (location: MapLocation): mapkit.MarkerAnnotation => {
        const mapkit = mapkitRef.current;
        if (!mapkit) {
          throw new Error("MapKit not available");
        }

        const annotation = new mapkit.MarkerAnnotation(
          new mapkit.Coordinate(location.latitude, location.longitude),
          {
            title: location.title,
            subtitle: location.subtitle,
            color: location.color,
            clusteringIdentifier: location.clusteringIdentifer ?? "all",
          },
        );

        annotation.callout = {};

        if (renderCalloutContent) {
          annotation.callout.calloutContentForAnnotation = () => syncRender(renderCalloutContent(location));
        }
        if (renderCalloutLeftAccessory) {
          annotation.callout.calloutLeftAccessoryForAnnotation = () => syncRender(renderCalloutLeftAccessory(location));
        }
        if (renderCalloutRightAccessory) {
          annotation.callout.calloutRightAccessoryForAnnotation = () => syncRender(renderCalloutRightAccessory(location));
        }

        return annotation;
      },
      [renderCalloutContent, renderCalloutRightAccessory, renderCalloutLeftAccessory],
    );

    const createAnnotations = useCallback(
      (locations: MapLocation[]): mapkit.MarkerAnnotation[] => {
        return locations?.map(createAnnotation);
      },
      [createAnnotation],
    );

    const updateAnnotations = useCallback(
      (locations: MapLocation[]) => {
        const map = mapInstanceRef.current;
        const mapkit = mapkitRef.current;
        if (!map || !mapkit) return;

        // Remove existing annotations
        if (annotationsRef.current.length > 0) {
          map.removeAnnotations(annotationsRef.current);
        }

        if (isLoading) {
          return;
        }

        // Create and add new annotations
        const newAnnotations = createAnnotations(locations);
        annotationsRef.current = newAnnotations;

        if (newAnnotations.length > 0) {
          map.addAnnotations(newAnnotations);

          // Auto-center map to show all annotations
          const region = calculateRegionForAnnotations(mapkit, newAnnotations);
          if (region) {
            map.region = region;
            const mapkitPadding = new mapkit.Padding(
              padding.top,
              padding.right,
              padding.bottom,
              padding.left,
            );
            map.showItems(newAnnotations, {
              animate: true,
              padding: mapkitPadding,
            });
          }
        }
      },
      [createAnnotations, padding, isLoading],
    );

    const getCurrentBounds = useCallback((): MapBounds | null => {
      const map = mapInstanceRef.current;
      if (!map) return null;

      const region = map.region;
      const center = region.center;
      const span = region.span;

      const northEast = {
        latitude: center.latitude + span.latitudeDelta / 2,
        longitude: center.longitude + span.longitudeDelta / 2,
      };

      const northWest = {
        latitude: center.latitude + span.latitudeDelta / 2,
        longitude: center.longitude - span.longitudeDelta / 2,
      };

      const southEast = {
        latitude: center.latitude - span.latitudeDelta / 2,
        longitude: center.longitude + span.longitudeDelta / 2,
      };

      const southWest = {
        latitude: center.latitude - span.latitudeDelta / 2,
        longitude: center.longitude - span.longitudeDelta / 2,
      };

      return {
        northEast,
        northWest,
        southEast,
        southWest,
        center: {
          latitude: center.latitude,
          longitude: center.longitude,
        },
      };
    }, []);

    // Initialize map when token changes
    useEffect(() => {
      const initializeMap = async () => {
        if (!token || !mapRef.current) {
          return;
        }

        // Clean up existing map
        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
        }

        try {
          // Load MapKit JS and get the instance
          const mapkit = await loadMapKitScript();
          mapkitRef.current = mapkit;

          // Initialize MapKit
          mapkit.init({
            authorizationCallback: (done) => done(token),
            language: language ?? i18n.language,
          });

          const mapProps: mapkit.MapConstructorOptions = {
            mapType,
            showsCompass,
            showsMapTypeControl,
            showsZoomControl,
            showsUserLocationControl,
            showsPointsOfInterest,
            showsScale,
          };

          if (center && center.latitude && center.longitude) {
            mapProps.center = new mapkit.Coordinate(center.latitude, center.longitude);
          } else {
            // Center on the US by default
            mapProps.center = new mapkit.Coordinate(DEFAULT_MAP_COORDINATES.lat, DEFAULT_MAP_COORDINATES.lng);
          }

          // Create map instance
          const map = new mapkit.Map(mapRef.current, mapProps);

          mapInstanceRef.current = map;
          mapkit.language = language ?? i18n.language;
          setIsLoaded(true);
          onMapLoad?.(map);
        } catch (error) {
          console.error("Failed to initialize Apple Maps:", error);
        }
      };

      initializeMap();

      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
        }
        mapkitRef.current = null;
      };
    }, [
      token,
      mapType,
      showsCompass,
      showsMapTypeControl,
      showsZoomControl,
      showsUserLocationControl,
      showsPointsOfInterest,
      showsScale,
      language,
      i18n.language,
    ]);

    useEffect(() => {
      if (isLoaded) {
        updateAnnotations(locations);
      }
    }, [isLoaded, locations, center, updateAnnotations]);

    useImperativeHandle(ref, () => ({
      getCurrentBounds,
    }));

    return (
      <div
        style={style}
        ref={mapRef}
        className={`${styles["MapWrapper"]} ${className}`}
        role="presentation"
      >
        { isLoading && <Skeleton className={styles["MapOverlaySkeleton"]} /> }
        <div className={styles["MapOverlayButtons"]}>
          { onSearchArea && (
            <Button
              testId="test__redo-map-search-button"
              disabled={isLoading}
              size="large"
              onClick={() => {
                onSearchArea(getCurrentBounds());
              }}
            >
              <Shelf spacing="mini" className={styles["RedoSearchOnSpinner"]}>
                {isLoading && <Spinner className="ReDoSearchOnMapSpinner" />}
                <span>{translate("APPLE_MAP.reDoSearchOnMap")}</span>
              </Shelf>
            </Button>
          )}
        </div>
      </div>
    );
  },
);

export default AppleMap;
